import {Component, Element, h, Listen, Prop} from '@stencil/core';
import {getGuidedSearchCategory, pushToDataLayer} from '../util';
import type {GuidedSearchCompleted} from '../../guided/api';
import type {RedirectionBannerEvent} from '../../widgets/redirection-banner/RedirectionBannerEvent';


/*

Items marked with ** are extending the current proposal and are not part of any name!


Action: when user goes to a Quick Link

type:quick-link###
sectionName:{sectionName}###
clickType:{clickType}###
searchKeyword:{searchKeyword}###
redirectionUrl:{openUrl}###
**position:{number}

info:
click type: left/right/enter
section name: technical name of the result section (
      'recommended', //to match with prod WMS
      'product-categories',
      'product-lines',
      'product-datasheets',
      'documents',
      'software',
      'faq',
      'support',
      'other', //to match with prod WMS
      ) //note: this will be different for APC as they have different sections
        //note: section name is technical, not translated!
---

Action: when user pressess enter to search for a keyword or uses a Suggestion

type:searchFor###
searchKeyword:{searchKeyword}###
redirectionSearchKeyword:{actualKeyword}###
clickType:{clickType}###
**position:{number}

info:
searchKeyword - what user put in the text box
actualKeyword - the keyword user selected to navigate to the search page

---
**Action: when user sees the result list

**type:guidedLoaded###
**performance:{time in ms}
**searchKeyword:{searchKeyword}

---
**Action: when user navigates the suggestions using cursors:

**type:guidedItemHighlighted###
**searchKeyword:{searchKeyword}
**openUrl:{when the user will be taken}

Additional info:
this event should be tracked in series and sent only the last item in serie, assuming 150ms time window
so that we avoid spamming with events

*/

@Component({
  tag: 'uiaas-search-analytics-reporting-guided',
})
export class UiaasSearchAnalyticsReportingGuidedComponent {
    @Prop() readonly language?: String;
    @Prop() readonly businessUnit?: String;
    @Prop() readonly articleTitle?: String;
    @Prop() readonly digitalPlatform?: String;
    @Prop() readonly digitalPlatformCountry?: String;
    @Prop() readonly env?: String;
    @Prop() readonly fromWebView?: Boolean;
    @Prop() readonly pageTopCategory?: String;
    @Prop() readonly pageCategory?: String;
    @Prop() readonly template?: String;
    @Prop() readonly pageTitle?: String;
    @Element() element: HTMLElement;


    private getCategory() {
        return this.element.querySelector('svs-search-bar')?.sourceParameter || '';
    }

    private readonly EVENTS_NAMES = {
      guidedSearchClick: 'guidedSearchClick',
      search: 'search'
    };

    @Listen('guided:SearchCompleted')
    guidedClickEventHandler(event: GuidedSearchCompleted & CustomEvent) {
        const isGuidedWithCategory = event.detail?.section ? `Guided Search: ${event.detail?.section}` : 'Guided Search';
        if (event.detail.event === this.EVENTS_NAMES.guidedSearchClick) {
          const dataLayer = {
            event: event.detail.event,
            guidedSearchCategory: getGuidedSearchCategory(event.target) + isGuidedWithCategory,
            guidedSearchKeyword: event.detail.term,
            destinationUrl: event.detail.openUrl,
            digitalPlatform: this.digitalPlatform || 'se main',
            site_search_category: 'Guided Search'
          };
          if (!event.detail.openUrl) delete dataLayer.destinationUrl;
          pushToDataLayer(dataLayer);
        }

        if (event.detail.event === this.EVENTS_NAMES.search) {
          pushToDataLayer({
            event: event.detail.event,
            site_search_category: `${getGuidedSearchCategory(event.target)}Guided Search`,
            guidedSearch: true,
            kw55: event.detail.term,
            refinedSearch: false,
            digitalPlatform: this.digitalPlatform || 'se main',
          });
        }
    }


    @Listen('search:guidedOptionSelected')
    dropdownOptionSelectedEventHandler(event: CustomEvent) {

        pushToDataLayer({
          event: 'OnSiteSearch',
          category: 'Guided Search',
          action: 'click',
          label: [
            'target:search category',
            `category:${event.detail?.id}`
          ].join('###'),
        });
    }

    @Listen('search:guidedOptionToggled')
    dropdownOpenEventHandler(event: CustomEvent) {
        if (event.detail) {
          pushToDataLayer({
            event: 'OnSiteSearch',
            category: 'Guided Search',
            action: 'click',
            label: 'target:dropdown',
          });
        }
    }

    @Listen('search:searchBarClicked')
    searchBarClickedEventHandler() {
        pushToDataLayer({
          event: 'OnSiteSearch',
          site_search_category: 'Search_bar',
          action: 'click',
          label: 'target:searchbar',
        });
    }

    @Listen('guided:footerClicked')
    seeAllClickEventHandler() {

        pushToDataLayer({
          event: 'OnSiteSearch',
          category: 'Guided Search',
          action: 'click',
          label: [
            'target:guided footer',
            `category:${this.getCategory()}`
          ].join('###'),
        });
    }


    @Listen('search:RedirectBannerClick')
    redirectionBannerClickedEventHandler() {
        pushToDataLayer({
          event: 'OnSiteSearch',
          category: 'Guided Search',
          action: 'click',
          label: [
            'target:redirection-banner',
            `category:${this.getCategory()}`,
          ].join('###')
        });
    }

    @Listen('guided:globalDecommissioningSelector', { target: 'window' })
    globalDecommissioningSelectorEventHandler(event: MouseEvent | CustomEvent<any>) {//eslint-disable-line
        pushToDataLayer({
          event: 'OnSiteSearch',
          category: 'country-selector-widget',
          action: 'click',
          label: [
            `source:${event.detail.source}`,
            `hits:${event.detail.hits}`,
            `keyword:${event.detail.keyword}`,
            `type:${event.detail.type}`
          ].join('###')
        });
    }

    @Listen('conversational-search:ga-cta-banner-clicked', { target: 'window' })
    conversationalSearchCtaBannerEventHandler(event: MouseEvent | CustomEvent<any>) { //eslint-disable-line
        if (event.detail.isGuided) {
          pushToDataLayer({
            event: 'genAI_search',
            search_keyword: event.detail.keyword,
            action: 'click',
            label: 'isRefinement:false'
          });
        }
    }

    @Listen('guided:historyClicked', { target: 'window' })
    guidedHistoryClickEventHandler(event: GuidedSearchCompleted & CustomEvent) {
        const dataLayer = {
          event: this.EVENTS_NAMES.guidedSearchClick,
          guidedSearchCategory: 'Guided Search: Search history',
          guidedSearchKeyword: event.detail,
          site_search_category: 'Guided Search',
          digitalPlatform: this.digitalPlatform || 'se main',
        };
        pushToDataLayer(dataLayer);
    }

    @Listen('guided:voiceSearchMicrophoneClicked')
    guidedVoiceSearchEventHandler(event: CustomEvent) {
        pushToDataLayer({
          event: 'OnSiteSearch',
          site_search_category: event.detail.category || '',
          site_search_action: event.detail.action || '',
          site_search_label: event.detail.label || '',
          site_search_keyword: event.detail.keyword || null
        });
    }

    @Listen('guided:voiceSearchWasPromotedCtaClicked')
    guidedVoiceSearchPromotionEventHandler(event: CustomEvent) {
        pushToDataLayer({
          event: 'cta_click',
          cta_location: event.detail.cta_location || '',
          cta_name: event.detail.cta_name || '',
          cta_link: event.detail.cta_link || '',
          cta_category: event.detail.cta_category || '',
          site_search_label: event.detail.label || '',
          site_search_keyword: event.detail.keyword || null
        });
    }

    render() {
        return <slot/>;
    }
}
